// Dashboard · Recent Leads table. Pulled out of Dashboard.jsx so the giant
// action-cell markup and nurture toggle stop dwarfing the tab shell.
import React from "react";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Switch } from "../ui/switch";
import {
  Mail, MessageCircle, ExternalLink, FileText, Sparkles,
  ShieldCheck, CheckCircle2, ClipboardList,
} from "lucide-react";
import CompactBenchmarkPill from "../CompactBenchmarkPill";
import { API } from "../../lib/api";

const NURTURE_LABEL = {
  scheduled: "scheduled",
  draft: "draft",
  cancelled: "cancelled",
  completed: "done",
};

export default function LeadsTable({
  leads,
  nurtureStatus,
  benchmarks,
  onOpenNurture,
  onOpenReview,
  onOpenBrief,
  onOpenProposal,
}) {
  return (
    <Card className="card-elevated p-0 lg:col-span-2 overflow-hidden">
      <div className="p-5 border-b border-border/60">
        <div className="overline">Recent leads</div>
        <h3 className="font-sans-ui font-semibold text-lg mt-1">Latest qualified conversations</h3>
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Company</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Country / Industry / Function</TableHead>
              <TableHead className="text-right">Score</TableHead>
              <TableHead className="text-center">Nurture</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {leads.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground py-10">
                  No leads yet. Share your Growth Campaign™ from the Campaigns tab.
                </TableCell>
              </TableRow>
            )}
            {leads.map((l) => {
              const nurtureLabel = NURTURE_LABEL[nurtureStatus[l.id]] || "off";
              const approved = (l.report_status || "approved") === "approved";
              return (
                <TableRow key={l.id} data-testid={`lead-row-${l.id}`}>
                  <TableCell>
                    <div className="font-medium">{l.company}</div>
                    <div className="text-xs text-muted-foreground">{l.name}{l.position ? ` · ${l.position}` : ""}</div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm flex items-center gap-1"><Mail className="w-3 h-3 text-accent" /> {l.email}</div>
                    <div className="text-xs text-muted-foreground flex items-center gap-1 mt-1"><MessageCircle className="w-3 h-3 text-accent" /> {l.mobile}</div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">{l.country}</div>
                    <div className="text-xs text-muted-foreground">{l.industry} · {l.function}</div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="font-display text-2xl text-accent">{l.results?.overall_score}</div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{l.results?.risk_level} risk</div>
                    <div className="mt-1">
                      <CompactBenchmarkPill data={benchmarks[l.id]} testId={`lead-benchmark-${l.id}`} />
                    </div>
                  </TableCell>
                  <TableCell className="text-center">
                    {l.unlocked ? (
                      <div className="inline-flex flex-col items-center gap-1">
                        <Switch
                          data-testid={`lead-nurture-toggle-${l.id}`}
                          checked={["draft", "scheduled"].includes(nurtureStatus[l.id])}
                          onCheckedChange={() => onOpenNurture(l)}
                          aria-label="Toggle nurture sequence"
                        />
                        <span className="text-[9px] uppercase tracking-widest text-muted-foreground" data-testid={`lead-nurture-status-${l.id}`}>
                          {nurtureLabel}
                        </span>
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <div className="mb-1.5 flex justify-end">
                      {approved ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] uppercase tracking-widest bg-emerald-500/15 border border-emerald-500/40 text-emerald-300" data-testid={`lead-report-status-${l.id}`}>
                          <CheckCircle2 className="w-2.5 h-2.5" /> Approved
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] uppercase tracking-widest bg-amber-500/15 border border-amber-500/40 text-amber-300" data-testid={`lead-report-status-${l.id}`}>
                          <ShieldCheck className="w-2.5 h-2.5" /> Review
                        </span>
                      )}
                    </div>
                    <Button variant="ghost" size="sm" title="Review Executive Growth Report™" data-testid={`lead-review-${l.id}`} onClick={() => onOpenReview(l)}>
                      <ShieldCheck className="w-4 h-4 text-accent" />
                    </Button>
                    <Button variant="ghost" size="sm" title="AI Meeting Brief™" data-testid={`lead-brief-${l.id}`} onClick={() => onOpenBrief(l)} disabled={!approved}>
                      <ClipboardList className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" title="Generate AI proposal" data-testid={`lead-proposal-${l.id}`} onClick={() => onOpenProposal(l)}>
                      <Sparkles className="w-4 h-4 text-accent" />
                    </Button>
                    <a href={`/report/${l.id}`} target="_blank" rel="noreferrer" data-testid={`lead-view-${l.id}`}>
                      <Button variant="ghost" size="sm" title="View full report">
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                    </a>
                    <a href={`${API}/leads/${l.id}/pdf`} target="_blank" rel="noreferrer" data-testid={`lead-pdf-${l.id}`}>
                      <Button variant="ghost" size="sm" title="Download PDF">
                        <FileText className="w-4 h-4" />
                      </Button>
                    </a>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}
